import React, { useEffect, useMemo, useRef, useState } from 'react';
import PriceChartingWidget from '../../components/PriceChartingWidget/PriceChartingWidget';
import { Certs } from '../api/broker';
import { suggestedPrice, gradeNumber, formatMoney } from './CertCells';
import './CreateListingPanel.css';
import AutoPriceInfo from './AutoPriceInfo';

const STEP_PCT = 10;
// New listings start this far above the PriceCharting comp.
const DEFAULT_MARKUP_PCT = 20;
// % pill colour: red at +10% and up, green below −10%, yellow in between.
const HIGH_MARKUP_PCT = 10;
const LOW_MARKUP_PCT = -10;
// Estimated shipping, folded into the listed price (buyers see free shipping).
const SHIPPING_EST = 5.99;
// A just-added cert gets its card match and PriceCharting estimate in the
// background (cert.inserted -> cert.card.matched). Re-read the cert this often
// until the estimate shows up, and give up after this long.
const PRICING_POLL_MS = 3000;
const PRICING_POLL_MAX_MS = 30 * 1000;

// Signed % difference from the estimate, e.g. "+12%", "−4.5%", "0%".
const formatPct = (pct) => {
    const rounded = Math.round(pct * 10) / 10;
    if (rounded === 0) return '0%';
    const abs = Math.abs(rounded);
    return `${rounded > 0 ? '+' : '−'}${Number.isInteger(abs) ? abs : abs.toFixed(1)}%`;
};

// Quick "list this cert" card that opens beside the scanner bar: cert, card and
// PriceCharting details up top, then price + auto-price + List. On wide screens
// the cert image moves out into its own column, so it's scanner | details | image.

// With `listing` (an active listing with an id) the panel edits it instead:
// same layout, prefilled from the listing, and saves via PATCH.
const CreateListingPanel = ({ cert: certProp, listing, onClose, onList, onUpdate }) => {
    const inputRef = useRef(null);
    const [freshCert, setFreshCert] = useState(null);
    const [pricingPending, setPricingPending] = useState(false);
    // The latest copy of this cert from polling (card + pricing filled in), over the one passed in.
    const cert = useMemo(
        () => (certProp && freshCert?.cert_number === certProp.cert_number ? { ...certProp, ...freshCert } : certProp),
        [certProp, freshCert]
    );
    const [price, setPrice] = useState('');
    const [autoPrice, setAutoPrice] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);

    const certNumber = cert?.cert_number;
    const suggested = suggestedPrice(cert);
    const editing = !!listing?.listing_id;

    // Reset whenever a different cert is opened.
    useEffect(() => {
        if (!cert) return;
        if (editing) {
            // Listed price = payout + estimated shipping, so back the shipping out.
            const payout = Math.max(0, Number(listing.price || 0) - SHIPPING_EST);
            setPrice(payout ? payout.toFixed(2) : '');
            setAutoPrice(!!listing.auto_price);
        } else {
            setPrice(suggested ? (Number(suggested) * (1 + DEFAULT_MARKUP_PCT / 100)).toFixed(2) : '');
            setAutoPrice(false);
        }
        setBusy(false);
        setError(null);
        setDone(false);
        setTimeout(() => inputRef.current?.select(), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [certNumber, editing]);

    // No estimate yet (a just-added cert): poll until the background card match
    // and PriceCharting fetch land, then show them.
    useEffect(() => {
        setFreshCert(null);
        if (!certNumber || certProp?.pricing_data) {
            setPricingPending(false);
            return undefined;
        }
        let cancelled = false;
        let timer = null;
        const startedAt = Date.now();
        setPricingPending(true);

        const poll = async () => {
            try {
                const found = (await Certs.getByNumber(certNumber))?.certs?.[0];
                if (cancelled) return;
                if (found) setFreshCert(found);
                if (found?.pricing_data) {
                    setPricingPending(false);
                    return;
                }
            } catch { /* try again next tick */ }
            if (cancelled) return;
            if (Date.now() - startedAt >= PRICING_POLL_MAX_MS) {
                setPricingPending(false);
                return;
            }
            timer = setTimeout(poll, PRICING_POLL_MS);
        };
        timer = setTimeout(poll, PRICING_POLL_MS);
        return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [certNumber]);

    // When the estimate arrives after the panel opened, fill in the default
    // price unless the broker already typed one.
    useEffect(() => {
        if (!editing && suggested && price === '') {
            setPrice((Number(suggested) * (1 + DEFAULT_MARKUP_PCT / 100)).toFixed(2));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggested]);

    useEffect(() => {
        if (!cert) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.stopImmediatePropagation();
                onClose();
            }
        };
        // Capture phase so Escape closes this before it reaches the scanner.
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [cert, onClose]);

    if (!cert) return null;

    const card = cert.card || {};
    const set = card.set || {};
    const name = card.name || cert.name || `#${certNumber}`;
    const grade = gradeNumber(cert);
    const image = cert.cert_images?.front_image_url;
    const valid = price !== '' && !Number.isNaN(parseFloat(price)) && parseFloat(price) > 0;
    const estimate = Number(suggested) || 0;
    const pct = valid && estimate ? (parseFloat(price) / estimate - 1) * 100 : null;
    const listPrice = valid ? (parseFloat(price) + SHIPPING_EST).toFixed(2) : null;

    // Move to the next STEP_PCT-of-estimate mark above/below the current price.
    const stepPct = (dir) => {
        if (!estimate) return;
        // Prices are rounded to cents, so a price "at" +10% may compute as +9.98%.
        // Treat anything within that rounding error as on the mark, so a step
        // always moves to the next one.
        const pos = (pct ?? 0) / STEP_PCT;
        const eps = Math.min(0.4, 0.6 / estimate / STEP_PCT + 1e-6);
        const next = (dir > 0 ? Math.floor(pos + eps) + 1 : Math.ceil(pos - eps) - 1) * STEP_PCT;
        const value = Math.max(0.01, estimate * (1 + next / 100));
        setPrice(value.toFixed(2));
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!valid || busy || done) return;
        setBusy(true);
        setError(null);
        try {
            if (editing) {
                await onUpdate({ cert, listing, price: listPrice, autoPrice });
            } else {
                await onList({ cert, price: listPrice, autoPrice });
            }
            setDone(true);
            setTimeout(onClose, 900);
        } catch (err) {
            setError(err.message);
            setBusy(false);
        }
    };

    const facts = [
        ['Set', set.series && set.series !== set.name ? `${set.series}: ${set.name}` : set.name || cert.set],
        ['Number', card.number || cert.set_number],
        ['Year', cert.year],
        ['Rarity', card.rarity],
        ['Artist', card.artist],
    ].filter(([, v]) => v !== undefined && v !== null && v !== '');

    return (
        <>
        <div className="create-listing-panel" role="dialog" aria-label={`${editing ? 'Edit listing for' : 'List'} ${name}`}>
            <button type="button" className="create-listing-close" onClick={onClose} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                </svg>
            </button>

            <div className="create-listing-hero">
                <div className="create-listing-image">
                    {image ? <img src={image} alt={name} /> : <span aria-hidden="true">—</span>}
                </div>
                <div className="create-listing-title">
                    <div className="create-listing-name-row">
                        <h3>{name}</h3>
                        {grade && <strong className="create-listing-grade">PSA {grade}</strong>}
                    </div>
                    <div className="create-listing-chips">
                        <a
                            href={`https://www.psacard.com/cert/${certNumber}/psa`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="create-listing-cert"
                        >
                            #{certNumber}
                        </a>
                        {editing && <span className="create-listing-live">Listed</span>}
                    </div>
                </div>
            </div>

            {facts.length > 0 && (
                <dl className="create-listing-facts">
                    {facts.map(([k, v]) => (
                        <div key={k}>
                            <dt>{k}</dt>
                            <dd>{v}</dd>
                        </div>
                    ))}
                </dl>
            )}

            <div className="create-listing-pricing">
                <PriceChartingWidget cert={cert} pending={pricingPending} />
                <div className="create-listing-market">
                    {/* PriceCharting comp for this cert's grade (what the % pill compares to) */}
                    <div className="create-listing-estimate">
                        <span className="create-listing-estimate-amt">
                            {estimate ? `$${estimate % 1 === 0 ? estimate.toFixed(0) : estimate.toFixed(2)}` : '—'}
                        </span>
                        <span className="create-listing-estimate-label">Estimate</span>
                    </div>
                </div>
            </div>

            <form className="create-listing-form" onSubmit={submit}>
                {/* Wholesale price label, then amount box, ±10% paddles, % vs comp, then
                    the shipping and total payout line items — all left-aligned. */}
                <div className="create-listing-entry">
                    <label htmlFor="create-listing-payout">Wholesale Price</label>
                    <div className="create-listing-entry-row">
                    <div className="create-listing-price">
                        <span className="create-listing-currency">$</span>
                        <input
                            ref={inputRef}
                            id="create-listing-payout"
                            type="text"
                            inputMode="decimal"
                            value={price}
                            onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ''))}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowUp') { e.preventDefault(); stepPct(1); }
                                if (e.key === 'ArrowDown') { e.preventDefault(); stepPct(-1); }
                            }}
                            disabled={busy || done}
                        />
                    </div>
                    <span className="create-listing-steppers">
                        <button type="button" onClick={() => stepPct(1)} disabled={busy || done || !estimate}
                            aria-label={`Raise ${STEP_PCT}%`} title={`+${STEP_PCT}%`}>▲</button>
                        <button type="button" onClick={() => stepPct(-1)} disabled={busy || done || !estimate}
                            aria-label={`Lower ${STEP_PCT}%`} title={`−${STEP_PCT}%`}>▼</button>
                    </span>
                    <span
                        className={pct === null ? 'create-listing-pct empty' : `create-listing-pct ${pct >= HIGH_MARKUP_PCT ? 'high' : pct < LOW_MARKUP_PCT ? 'low' : 'mid'}`}
                        title="Difference from the PriceCharting comp"
                    >
                        {pct === null ? '' : formatPct(pct)}
                    </span>
                    </div>

                    <div className="create-listing-line">
                        <span className="create-listing-line-amt">+{formatMoney(SHIPPING_EST)}</span>
                        <span>Shipping <span className="create-listing-muted">est.</span></span>
                    </div>
                    <div className="create-listing-line total">
                        <span className="create-listing-line-amt">{listPrice ? formatMoney(listPrice) : '—'}</span>
                        <span>Total Payout <span className="create-listing-muted">(buyers see free shipping)</span></span>
                    </div>
                </div>

                <label className="create-listing-toggle">
                    <input
                        type="checkbox"
                        checked={autoPrice}
                        onChange={(e) => setAutoPrice(e.target.checked)}
                        disabled={busy || done}
                    />
                    <span className="create-listing-switch" aria-hidden="true" />
                    <span>Auto-price<AutoPriceInfo /></span>
                </label>

                {error && <div className="create-listing-error">{error}</div>}

                <button
                    type="submit"
                    className={`create-listing-submit ${done ? 'done' : ''}`}
                    disabled={!valid || busy || done}
                >
                    {editing
                        ? (done ? '✓ Updated' : busy ? 'Saving…' : `Update to ${listPrice ? `${formatMoney(listPrice)} Payout` : '…'}`)
                        : (done ? '✓ Listed' : busy ? 'Listing…' : `List${listPrice ? ` for ${formatMoney(listPrice)} Payout` : ''}`)}
                </button>
            </form>
        </div>

        {/* Wide screens: the cert image gets its own column to the right of the panel. */}
        <div className="create-listing-image-panel" aria-hidden="true">
            {image ? <img key={certNumber} src={image} alt="" /> : <span>No image yet</span>}
        </div>
        </>
    );
};

export default CreateListingPanel;
